/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  ClockCircleFilled,
  DownloadOutlined,
  FilterOutlined,
  LoadingOutlined,
  MoreOutlined,
  PlayCircleFilled,
} from '@ant-design/icons';
import { Button, Dropdown, Select, Tag, Tooltip } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from 'styles/components/surveyNameStatus.module.scss';
import { download } from 'utils/api';
import { DOWNLOAD_TYPE } from 'utils/constants';
import { getTextFromFrequency } from 'utils/utils';
import LoadingFullscreen from './loadingFullscreen';

const SurveyNameStatus = ({
  survey,
  showOptions = false,
  departments,
  departmentsLoading,
  onDepartmentChange,
  getDepartments = () => { },
  departmentId,
}) => {
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [searchParams] = useSearchParams();
  const bot = searchParams.get('bot');
  const { id } = useParams();
  const status =
    survey.deadline && moment(survey.deadline).isBefore(moment())
      ? 'CLOSED'
      : survey.status;

  const downloadODForCSV = (type = DOWNLOAD_TYPE.PDF, withComments = false) => {
    if (loadingDownload !== null) return;
    setLoadingDownload(type);
    let queryString = '';
    if (departmentId) {
      queryString = `department=${departmentId}`;
    }
    if (withComments) {
      queryString += (queryString ? '&' : '') + 'withcomments=true';
    }
    download({
      surveyId: id,
      type,
      ...(queryString ? { query: queryString } : {}),
    })
      .then((res) => {
        window.open(res.url, '_blank');
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadingDownload(null);
      });
  };

  const items = [
    {
      label: (
        <a onClick={() => downloadODForCSV(DOWNLOAD_TYPE.PDF, true)} style={{ textDecoration: 'none' }}>
          {loadingDownload === DOWNLOAD_TYPE.PDF ? (
            <LoadingOutlined />
          ) : (
            <DownloadOutlined />
          )}
          &nbsp;Download with the comments (.pdf)
        </a>
      ),
      key: '12',
    },
    {
      label: (
        <a onClick={() => downloadODForCSV(DOWNLOAD_TYPE.PDF)} style={{ textDecoration: 'none' }}>
          {loadingDownload === DOWNLOAD_TYPE.PDF ? (
            <LoadingOutlined />
          ) : (
            <DownloadOutlined />
          )}
          &nbsp;Download (.pdf)
        </a>
      ),
      key: '0',
    },
    {
      label: (
        <a onClick={() => downloadODForCSV(DOWNLOAD_TYPE.CSV)} style={{ textDecoration: 'none' }}>
          {loadingDownload === DOWNLOAD_TYPE.CSV ? (
            <LoadingOutlined />
          ) : (
            <DownloadOutlined />
          )}{' '}
          Download (.csv)
        </a>
      ),
      key: '1',
    },
  ];

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.left}>
          <div className={styles.itemName}>{survey.name}</div>
          <div>
            <div className={styles.itemStatus}>
              <Tag
                color={status === 'LIVE' ? 'green' : 'volcano'}
                className={styles.itemStatusInner}
              >
                <div className={`${styles.circle} ${styles[status]}`}></div>
                {status}
              </Tag>
              {survey.anonymous && (
                <Tooltip title="Anonymous survey">
                  <Tag color="purple" className={styles.anonymous}>
                    {bot === 'true' ? ' ' : '🥷'}&nbsp;Anonymous
                  </Tag>
                </Tooltip>
              )}
              {survey.isRecurring && (
                <Tooltip title="Recurring Survey">
                  <Tag color="blue" className={styles.frequency}>
                    🔄&nbsp;{getTextFromFrequency(survey.frequency)}
                  </Tag>
                </Tooltip>
              )}
            </div>
            <div className={styles.itemDates}>
              {survey.publishedAt && (
                <div>
                  <Tooltip title="Published At" placement={['bottom']}>
                    <PlayCircleFilled />{' '}
                    {moment(survey.publishedAt).format('L')}
                  </Tooltip>
                </div>
              )}
              {survey.deadline && (
                <div className={styles.deadline}>
                  <Tooltip title="Deadline" placement={['bottom']}>
                    <ClockCircleFilled /> {moment(survey.deadline).format('L')}
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
        {showOptions && (
          <div className={styles.right}>
            {
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {departments && departments.status === 'LIVE' && bot !== 'true' ? (
                  <a
                    href={departments.link}
                    style={{ textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      type="link"
                      style={{ display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        getDepartments();
                      }}
                    >
                      <img
                        src="/img/logo/linky.png"
                        width={22}
                        height={22}
                        alt="linky"
                      />
                      &nbsp;
                      <span style={{ textDecoration: 'none' }}>Open Linky</span>
                    </Button>
                  </a>
                ) : (
                  <></>
                )}
                <Select
                  value={departmentId ?? null}
                  onChange={onDepartmentChange}
                  icon={<FilterOutlined />}
                  placeholder={'Filter by department'}
                  loading={departmentsLoading}
                  options={[
                    {
                      value: null,
                      label: (
                        <label
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <div
                            className={styles.colorDepartment}
                            style={{ backgroundColor: 'black' }}
                          ></div>
                          <span>All respondents</span>
                        </label>
                      ),
                    },
                    ...(departments && departments.status === 'LIVE'
                      ? [
                        {
                          label: (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <img
                                src="/img/logo/linky.png"
                                width={22}
                                height={22}
                                alt="linky"
                              />
                              &nbsp; Departments from Linky
                            </label>
                          ),
                          value: 'linky',
                          options:
                            departments.departments.length > 0
                              ? departments.departments.map((d) => ({
                                value: d._id,
                                label: (
                                  <label
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div
                                      className={styles.colorDepartment}
                                      style={{ backgroundColor: d.color }}
                                    ></div>
                                    <span>{`${d.value}`}</span>
                                  </label>
                                ),
                              }))
                              : [
                                {
                                  value: undefined,
                                  label: (
                                    <label>
                                      <span>
                                        No departments are defined in Linky
                                      </span>
                                    </label>
                                  ),
                                  disabled: true,
                                },
                              ],
                        },
                      ]
                      : [
                        {
                          label: (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <img
                                src="/img/logo/linky.png"
                                width={22}
                                height={22}
                                alt="linky"
                              />
                              &nbsp; Departments from Linky
                            </label>
                          ),
                          value: 'linky',
                          options: [
                            {
                              label: (
                                <div>
                                  <a
                                    href="https://buddieshr.com/linky?ref=pulsy_app"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: '#2d80e6' }}
                                  >
                                    {departments?.status === 'UNINSTALLED'
                                      ? 'Re-install Linky'
                                      : 'Install Linky'}
                                  </a>{' '}
                                  &nbsp;
                                  <span style={{ color: 'black' }}>
                                    to filter by department
                                  </span>
                                </div>
                              ),
                              value: undefined,
                              disabled: true,
                            },
                          ],
                        },
                      ]),
                  ]}
                  style={{ width: 240, marginRight: 10 }}
                  popupMatchSelectWidth={false}
                />
              </div>
            }
            {bot !== 'true' && <Dropdown menu={{ items }} trigger={['click']}>
              <Button onClick={(e) => e.preventDefault()}>
                <MoreOutlined />
              </Button>
            </Dropdown>}
          </div>
        )}
      </div>
      {loadingDownload && <LoadingFullscreen />}
    </>
  );
};

export default SurveyNameStatus;
